/**
 * Audience split — the bridge from marketing into the two real consoles:
 *   • Pet Owner card  → /user/login  (the Pet-Owner console)
 *   • Vendor card     → /login       (the Vendor onboarding + workspace)
 * Both also expose a "Book a demo" path for guided onboarding.
 */
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, PawPrint, Store, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Audience = {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  blurb: string;
  perks: string[];
  primaryLabel: string;
  onPrimary: () => void;
  testid: string;
};

export function LandingAudience() {
  const navigate = useNavigate();

  const audiences: Audience[] = [
    {
      icon: PawPrint,
      eyebrow: 'Pet owners',
      title: 'Keep every pet happy & healthy',
      blurb: 'One home for your pets, their care history, and every upcoming booking.',
      perks: [
        'Profiles for all your pets',
        'Book vets, walkers, grooming & more',
        'Reminders so you never miss care',
      ],
      primaryLabel: 'Enter Pet Owner console',
      onPrimary: () => navigate('/user/login'),
      testid: 'audience-owner',
    },
    {
      icon: Store,
      eyebrow: 'Service vendors',
      title: 'Grow your pet-care business',
      blurb: 'List your services, manage availability, and fill your calendar with happy clients.',
      perks: [
        'Simple guided onboarding & KYC',
        'Manage services & availability',
        'Track bookings in one dashboard',
      ],
      primaryLabel: 'List your business',
      onPrimary: () => navigate('/login'),
      testid: 'audience-vendor',
    },
  ];

  return (
    <section id="audience" className="scroll-mt-24 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-5">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Built for both ends of the leash
          </h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Whether you're booking care or providing it, there's a friendly home for you here.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {audiences.map((a) => (
            <div
              key={a.testid}
              className="group flex flex-col rounded-card border-2 border-brand-outline-variant/20 bg-card p-7 shadow-clay transition-all duration-normal ease-bounce hover:-translate-y-1 hover:shadow-clay-lg sm:p-8"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-button accent-gradient text-white shadow-clay-sm transition-transform duration-normal ease-bounce group-hover:scale-110">
                  <a.icon className="h-6 w-6" />
                </span>
                <span className="text-sm font-bold uppercase tracking-wide text-primary">
                  {a.eyebrow}
                </span>
              </div>

              <h3 className="mt-5 font-heading text-2xl font-bold leading-tight">{a.title}</h3>
              <p className="mt-2 text-muted-foreground">{a.blurb}</p>

              <ul className="mt-5 space-y-2.5">
                {a.perks.map((perk) => (
                  <li key={perk} className="flex items-center gap-2.5 text-sm font-medium">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    {perk}
                  </li>
                ))}
              </ul>

              <div className="mt-7">
                <Button variant="gradient" className="w-full" onClick={a.onPrimary} data-testid={a.testid}>
                  {a.primaryLabel}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
